<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch, watchEffect } from 'vue'
import { twMerge } from 'tailwind-merge'
import ArrowDownImage from '@/assets/images/icons/arrow-down2-icon.webp'

const props = withDefaults(defineProps<{
  contentClass?: string
  visibleCount?: number
  gap?: number
  arrowHeight?: number
  reserveArrowSpace?: boolean
}>(), {
  contentClass: '',
  visibleCount: 4,
  gap: 20,
  arrowHeight: 24,
  reserveArrowSpace: true,
})

const rootRef = ref<HTMLElement | null>(null)
const viewportRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)

const itemHeights = ref<number[]>([])
const firstVisible = ref(0)

const hasItems = computed(() => itemHeights.value.length > 0)
const lastVisible = computed(() =>
  Math.min(firstVisible.value + props.visibleCount - 1, Math.max(0, itemHeights.value.length - 1)),
)
const canScrollUp = computed(() => firstVisible.value > 0)
const canScrollDown = computed(() => lastVisible.value < itemHeights.value.length - 1)

function gapsFor(items: number) {
  return items > 1 ? (items - 1) * props.gap : 0
}

function sumHeights(i: number, j: number): number {
  const items = j - i + 1
  if (items <= 0) return 0

  const slice = itemHeights.value.slice(i, j + 1)
  const sum = slice.reduce((acc, h) => acc + (h ?? 0), 0)

  return sum + gapsFor(items)
}

const topOffsets = computed<number[]>(() => {
  const heights = itemHeights.value
  const out: number[] = new Array(heights.length + 1)
  out[0] = 0
  for (let i = 0; i < heights.length; i++) {
    out[i + 1] = out[i] + (heights[i] ?? 0) + props.gap
  }
  return out
})

function offsetForIndex(i: number): number {
  const idx = Math.max(0, Math.min(i, topOffsets.value.length - 1))
  return topOffsets.value[idx]
}

const viewportHeight = computed(() => {
  if (!hasItems.value) return 0
  if (itemHeights.value.length <= props.visibleCount) {
    return sumHeights(0, itemHeights.value.length - 1)
  }
  return sumHeights(firstVisible.value, firstVisible.value + props.visibleCount - 1)
})

const arrowsBlock = computed(() => props.arrowHeight * 2)
const totalHeight = computed(() => viewportHeight.value + arrowsBlock.value)
const translateY = computed(() => `-${offsetForIndex(firstVisible.value)}px`)

function stepDown() {
  if (!canScrollDown.value) return
  firstVisible.value = Math.min(
    firstVisible.value + 1,
    Math.max(0, itemHeights.value.length - props.visibleCount),
  )
}

function stepUp() {
  if (!canScrollUp.value) return
  firstVisible.value = Math.max(0, firstVisible.value - 1)
}

function onWheel(e: WheelEvent) {
  if (Math.abs(e.deltaY) < 1) return
  if (e.deltaY > 0) stepDown()
  else stepUp()
}

let touchStartY = 0
function onTouchStart(e: TouchEvent) {
  touchStartY = e.touches[0]?.clientY ?? 0
}
function onTouchEnd(e: TouchEvent) {
  const endY = e.changedTouches[0]?.clientY ?? touchStartY
  const dy = endY - touchStartY
  const threshold = 24
  if (Math.abs(dy) < threshold) return
  if (dy < 0) stepDown()
  else stepUp()
}

let listRO: ResizeObserver | null = null
let listMO: MutationObserver | null = null
let rafId: number | null = null

function scheduleMeasure() {
  if (rafId != null) return
  rafId = requestAnimationFrame(() => {
    rafId = null
    nextTick(snapshotHeights)
  })
}

function teardownObservers() {
  listRO?.disconnect()
  listRO = null
  listMO?.disconnect()
  listMO = null
  if (rafId != null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

function snapshotHeights() {
  const listEl = listRef.value
  if (!listEl) {
    itemHeights.value = []
    return
  }
  const children = Array.from(listEl.children) as HTMLElement[]
  const heights: number[] = []
  for (const el of children) {
    const h = el.offsetHeight
    if (h > 0) heights.push(h)
  }
  itemHeights.value = heights

  if (firstVisible.value > Math.max(0, heights.length - props.visibleCount)) {
    firstVisible.value = Math.max(0, heights.length - props.visibleCount)
  }
}

watchEffect((onCleanup) => {
  nextTick(() => {
    teardownObservers()

    const listEl = listRef.value
    if (!listEl) return

    listRO = new ResizeObserver(() => {
      scheduleMeasure()
    })
    listRO.observe(listEl)

    listMO = new MutationObserver(() => {
      scheduleMeasure()
    })
    listMO.observe(listEl, { childList: true })

    window.addEventListener('resize', scheduleMeasure, { passive: true })

    scheduleMeasure()
  })

  onCleanup(() => {
    window.removeEventListener('resize', scheduleMeasure)
    teardownObservers()
  })
})

onBeforeUnmount(() => {
  teardownObservers()
})

watch(() => props.visibleCount, async () => {
  await nextTick()
  snapshotHeights()
})
</script>

<template>
  <div
    ref="rootRef"
    :class="twMerge('relative w-[88px] select-none overflow-x-visible transition-all duration-300', contentClass)"
    :style="{ height: `${totalHeight}px` }"
  >
    <div
      class="absolute left-0 right-0 z-[2] flex items-end justify-center transition-all duration-300"
      :style="{ height: `${arrowHeight}px`, top: '0px' }"
    >
      <button
        v-show="canScrollUp"
        class="flex h-6 w-full rotate-180 items-center justify-center"
        @click="stepUp"
        aria-label="Scroll up"
        type="button"
      >
        <img
          class="h-6 w-auto"
          :src="ArrowDownImage"
          alt=""
        >
      </button>
    </div>

    <div
      ref="viewportRef"
      class="hide-scrollbar absolute left-0 right-0 overflow-y-hidden overflow-x-visible"
      :style="{ top: `${arrowHeight + 4}px`, height: `${viewportHeight + 12}px` }"
      @wheel.prevent.stop="onWheel"
      @touchstart.stop="onTouchStart"
      @touchmove.prevent.stop="() => {}"
      @touchend.stop="onTouchEnd"
    >
      <div
        ref="listRef"
        class="flex flex-col items-center transition-transform duration-300 will-change-transform"
        :style="{ gap: `${gap}px`, transform: `translateY(${translateY})` }"
      >
        <slot />
      </div>
    </div>

    <div
      class="absolute left-0 right-0 z-[2] flex items-start justify-center"
      :style="{ height: `${arrowHeight}px`, top: `${arrowHeight + 12 + viewportHeight}px` }"
    >
      <button
        v-show="canScrollDown"
        class="flex h-6 w-full items-center justify-center"
        @click="stepDown"
        aria-label="Scroll down"
        type="button"
      >
        <img
          class="h-6 w-auto"
          :src="ArrowDownImage"
          alt=""
        >
      </button>
    </div>
  </div>
</template>

<style scoped>
.hide-scrollbar {
  overflow-x: visible;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
